import androidx.compose.animation.AnimatedContentTransitionScope
import androidx.compose.animation.ExitTransition
import androidx.compose.animation.ExperimentalSharedTransitionApi
import androidx.compose.animation.SharedTransitionLayout
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavController
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.compose.rememberNavController
import androidx.navigation.toRoute
import models.TajMahal
import models.Wonder
import models.parse
import platform.Platform
import ui.screens.EditorialScreen
import ui.screens.NotInvitedScreen
import ui.screens.PhotoGallery
import ui.theme.ColorScheme
import ui.theme.Typography
import utils.AudioPlayer

expect fun runJsOnpopstate()
expect fun runJsAddPopStateListener(listener: (Any) -> Unit)
expect fun runJsRemovePopStateListener(listener: (Any) -> Unit)
expect fun runJsPushState(route: String)
expect fun runJsBack()
expect fun popBackWeb(onNavigateWeb: (String) -> Unit, onPopBackStack: () -> Unit)

var isHandlingPopState = false
var suppressJsPopState = false

@OptIn(ExperimentalSharedTransitionApi::class)
@Composable
fun App() {
    MaterialTheme(
        colorScheme = ColorScheme,
        typography = Typography,
    ) {
        val navigator = rememberNavController()
        LaunchedEffect(navigator) {
            runJsOnpopstate()
        }
        DisposableEffect(navigator) {
            val listener: (Any) -> Unit = { event ->
                if (suppressJsPopState) {
                    suppressJsPopState = false
                } else {
                    isHandlingPopState = true
                    popBackWeb(
                        onNavigateWeb = {
                            navigator.navigateWebAware(it.toRoute())
                        },
                        onPopBackStack = {
                            navigator.popBackStack()
                        }
                    )
                }
            }

            runJsAddPopStateListener(listener)
            onDispose {
                runJsRemovePopStateListener(listener)
            }
        }
        val audioPlayer = remember { AudioPlayer() }

        SharedTransitionLayout {

            NavHost(
                navController = navigator,
                startDestination = AppRoute.Wonder(TajMahal.name),
                enterTransition = { DefaultEnterTransition },
                exitTransition = { DefaultExitTransition },
                popEnterTransition = { DefaultPopEnterTransition },
                popExitTransition = { DefaultPopExitTransition },
            ) {
                composable<AppRoute.NotInvited> { backStackEntry ->
                    NotInvitedScreen(
                        onBack = {
                            suppressJsPopState = true
                            navigator.popBackStack()
                        }
                    )
                }

                composable<AppRoute.Wonder> { backStackEntry ->
                    val route = backStackEntry.toRoute<AppRoute.Wonder>()
                    val wonder = Wonder.parse(route.wonderId)
                    EditorialScreen(
                        wonder = wonder,
                        openNotInvitedScreen = {
                            suppressJsPopState = true
                            navigator.navigate(AppRoute.NotInvited)
                        },
                        openGalleryScreen = {
                            val platform = platform.platform
                            if (platform is Platform.Web) {
                                suppressJsPopState = false
                                isHandlingPopState = false
                                navigator.navigateWebAware(AppRoute.Gallery(TajMahal.name))
                            } else {
                                navigator.navigate(AppRoute.Gallery(TajMahal.name))
                            }
                        },
                        audioPlayer = audioPlayer,
                        animatedVisibilityScope = this,
                    )
                }

                composable<AppRoute.Gallery> { backStackEntry ->
                    val route = backStackEntry.toRoute<AppRoute.Gallery>()
                    PhotoGallery(
                        wonder = Wonder.parse(route.wonderId),
                        onBackClick = {
                            val platform = platform.platform
                            if (platform is Platform.Web) runJsBack() else navigator.popBackStack()
                        }
                    )
                }
            }
        }
    }
}

fun NavController.navigateWebAware(route: AppRoute) {
    println("Route: $route, isHandlingPopState: $isHandlingPopState")
    val isSharedTransitionRoute = route is AppRoute.Wonder
    if (!isHandlingPopState && !isSharedTransitionRoute) {
        if (route is AppRoute.Gallery) {
            runJsPushState("/Gallery")
        }
    }
    isHandlingPopState = false
    this.navigate(route)
}

fun String.toRoute(): AppRoute = when {
    startsWith("/Gallery") -> {
        AppRoute.Gallery(TajMahal.name)
    }
    // dst jika perlu
    else -> AppRoute.Wonder(TajMahal.name)
}

private val DefaultEnterTransition = fadeIn(tween(500))

val AnimatedContentTransitionScope<NavBackStackEntry>.DefaultExitTransition
    get() = fadeOut(tween(600, delayMillis = 100)) +
            ExitTransition.KeepUntilTransitionsFinished

private val DefaultPopEnterTransition = fadeIn(tween(200))

val AnimatedContentTransitionScope<NavBackStackEntry>.DefaultPopExitTransition
    get() = fadeOut(tween(500)) + ExitTransition.KeepUntilTransitionsFinished